import React, { useEffect } from 'react'
import LanguageStrings from '../../../../i18n/locales'
import { Box, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, styled, tableCellClasses } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { type HistoryARData } from '../../types'
import { fetchKpiHistoryAR } from '../../redux/actionCreators'
import { type HistoryARTableState } from '../../redux/HistoryARTableSlice'
import { type RootState } from '../../../../store'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import Pagination from '../../../../components/TablePagination'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import { type ToggleOption } from '../../../../components/GenericToggleButton'
import dayjs from 'dayjs'
import { convertDate, getDetailKeyword } from './kpiUtils'

const KpiDetailsStrings = LanguageStrings().AMIDataCollectionStrings.KpiDetails

interface Data {
  serialNumber: string
  assetType: string
  lastCommDate: string | null
  cliponSerialNumber: string | null
  shippedDate: string | null
  installedDate: string | null
  firstCommDate: string | null
}

const style = {
  width: '100%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0
}

type Order = 'asc' | 'desc'
interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
}

const headCells: readonly HeadCell[] = [
  {
    id: 'serialNumber',
    numeric: false,
    disablePadding: false,
    label: KpiDetailsStrings.SerialNumber
  },
  {
    id: 'assetType',
    numeric: false,
    disablePadding: false,
    label: KpiDetailsStrings.Asset_Type
  },
  {
    id: 'lastCommDate',
    numeric: false,
    disablePadding: false,
    label: KpiDetailsStrings.LastCommunicationDate
  },
  {
    id: 'cliponSerialNumber',
    numeric: false,
    disablePadding: false,
    label: KpiDetailsStrings.CliponSerialNumber
  },
  {
    id: 'shippedDate',
    numeric: false,
    disablePadding: false,
    label: KpiDetailsStrings.ShippedDate
  },
  {
    id: 'installedDate',
    numeric: false,
    disablePadding: false,
    label: KpiDetailsStrings.InstalledDate
  },
  {
    id: 'firstCommDate',
    numeric: false,
    disablePadding: false,
    label: KpiDetailsStrings.CommunicatedDate
  }
]

function EnhancedTableHead (props: any): any {
  const { order, orderBy, onRequestSort } = props
  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      // istanbul ignore next
      onRequestSort(event, newOrderBy)
    }
  return (
    <TableHead data-testid="table-header">
      <TableRow>
        {headCells.map((headCell: HeadCell) => (
          <StyledTableCell
            key={headCell.id}
            align={'left'}
            padding={'normal'}
          >
            <TableSortLabel
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                aria-label={headCell.label}
                data-testid="test-sort"
              >
                {headCell.label}
                {orderBy === headCell.id
                  ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                    )
                  : null}
              </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web'
  }
}))

interface Sort {
  order: Order
  orderBy: string
}

const StyledTableRow = styled(TableRow)(() => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

interface ParentProps {
  selectedBar: ToggleOption
  selectedDate: dayjs.Dayjs
  rowPerPage: number
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  sort: Sort
  setSort: React.Dispatch<React.SetStateAction<Sort>>
}

function HistoryTableComponent (props: AllProps): JSX.Element {
  const [assets, setAssets] = React.useState<HistoryARData[]>([])
  const selectedKpi = useSelector((state: RootState) => state.kpiType)

  const fetchAssets = (): void => {
    if (props.page >= 1) {
      const [detailKeyword, detailService] = getDetailKeyword(selectedKpi, props.selectedBar.value)
      props.fetchKpiHistoryAR(detailKeyword, dayjs(props.selectedDate).format('YYYY-MM-DD'), props.page - 1, props.rowPerPage, detailService)
    }
  }

  useEffect(() => {
    if (props.KpiHistoryARState.data !== undefined) {
      setAssets(props.KpiHistoryARState.data.assets)
    }
  }, [props.KpiHistoryARState])

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    newOrderBy: keyof Data
  ): void => {
    const isAsc = props.sort.orderBy === newOrderBy && props.sort.order === 'asc'
    const toggledOrder = isAsc ? 'desc' : 'asc'
    props.setSort({ order: toggledOrder, orderBy: newOrderBy })
  }

  if (props.KpiHistoryARState.data !== undefined && props.KpiHistoryARState.httpStatus === 200 && props.KpiHistoryARState.data.assets.length > 0) {
    return (
      <>
        <Box sx={style} className="devices">
          <Paper
            sx={{
              width: '100%',
              mb: 2,
              backgroundColor: '#272727',
              boxShadow: 'unset',
              color: '#F0F0F0',
              marginBottom: '0'
            }}
          >
            <TableContainer className="device-table">
              <Table
                sx={{ minWidth: 750, color: '#f0f0f0' }}
                aria-labelledby="tableTitle"
                size={'small'}
              >
                <EnhancedTableHead
                  order={props.sort.order}
                  orderBy={props.sort.orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {assets.map((row, index) => {
                    return (
                        <StyledTableRow
                          hover
                          tabIndex={-1}
                          key={index}
                          aria-label="asset-list-row"
                          sx={{ cursor: 'pointer' }}
                        >
                          <StyledTableCell sx={{ padding: '1.2px 16px', color: '#64C3FF' }}>{row.serialNumber}</StyledTableCell>
                          <StyledTableCell sx={{ padding: '1.2px 16px' }}>
                            {row.assetType.toUpperCase()}
                          </StyledTableCell>
                          <StyledTableCell sx={{ padding: '1.2px 16px' }}>
                            {props.selectedDate.toString()}
                          </StyledTableCell>
                          <StyledTableCell sx={{ padding: '1.2px 16px' }}>
                            {row.cliponSerialNumber}
                          </StyledTableCell>
                          <StyledTableCell sx={{ padding: '1.2px 16px' }}>
                          {convertDate(row.shippedDate)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ padding: '1.2px 16px' }}>
                          {convertDate(row.installedDate)}
                          </StyledTableCell>
                          <StyledTableCell sx={{ padding: '1.2px 16px' }}>
                          {convertDate(row.firstCommDate)}
                          </StyledTableCell>
                        </StyledTableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={props.KpiHistoryARState.data.total_count}
              page={props.page}
              setPage={props.setPage}
              rowsPerPage={props.rowPerPage}
              siblingCount={1}
              boundaryCount={1}
            />
          </Paper>
        </Box>
      </>
    )
  } else if (props.KpiHistoryARState.isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }} className="CircularprogressAmi">
        <CircularProgress />
      </div>
    )
  } else if (props.KpiHistoryARState.data !== undefined && props.KpiHistoryARState.httpStatus === 200 && props.KpiHistoryARState.data.assets.length === 0) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    )
  } else if (props.KpiHistoryARState.httpStatus === 401) {
    return (
      <div className="authError">
        <AuthError errorMessage="Unauthorized" />
      </div>
    )
  } else if (props.KpiHistoryARState.httpStatus === 403) {
    return (
      <div className="authError">
        <AuthError errorMessage="accessForbidden" />
      </div>
    )
  } else {
    return (
      <div className="authError" style={{ margin: '1em' }}>
        <AuthError errorMessage="cannotFetch" retry={fetchAssets} />
      </div>
    )
  }
}

interface DispatchToProps {
  fetchKpiHistoryAR: (
    kpiType: string,
    inputDateRead: string,
    page: number,
    size: number, service: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchKpiHistoryAR: (
    kpiType: string,
    inputDateRead: string,
    page: number,
    size: number, service: string) => dispatch(fetchKpiHistoryAR(kpiType, inputDateRead, page, size, service))
})

interface StateToProps {
  KpiHistoryARState: HistoryARTableState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  KpiHistoryARState: state.KpiHistoryARState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
type AllProps = ParentProps & PropsFromRedux

export default connector(HistoryTableComponent)
